import { useTranslation } from "react-i18next";
import {
  Box,
  Stack,
  Button,
  Dialog,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent
} from "@material-ui/core";
import { Close, PhotoLibrary } from "@material-ui/icons";
import { useModal } from "hooks/useModal";
import { ProcessedListingData } from "../../../../../@types/sharedSchema";
import { Image } from "components/Image";
import type { FileOrFilePreview, FilePreview } from "../../../../../@types/listings/fileData";
import type { Dispatch, SetStateAction } from "react";
import type { UploadedFile } from "./NewBulkMessageDialog";

type BulkMessageDialogImageListingUploadProps = {
  listing: ProcessedListingData;
  filesPreview: FilePreview[];
  selectedFiles: FileOrFilePreview[];
  setUploadedFile: Dispatch<SetStateAction<UploadedFile>>;
  setFilesPreview: Dispatch<SetStateAction<FilePreview[]>>;
  setSelectedFiles: Dispatch<SetStateAction<FileOrFilePreview[]>>;
};

export function BulkMessageDialogImageListingUpload({
  listing,
  filesPreview,
  selectedFiles,
  setUploadedFile,
  setFilesPreview,
  setSelectedFiles
}: BulkMessageDialogImageListingUploadProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { open, openModal, closeModal } = useModal();

  const handleAddImageFromListing = (url: string) => (): void => {
    const isAlreadyAdded = isImageSelected(url);

    if (isAlreadyAdded) return;

    const newFile = {
      id: url,
      src: url,
      name: url.split("/").pop() || "Listing Image",
      size: 0,
      type: "image/jpeg" as const
    };

    setFilesPreview([newFile]);
    setSelectedFiles([newFile]);

    setUploadedFile({ src: url, name: newFile.name });
  };

  const handleRemoveImageFromListing = (url: string) => (e: React.MouseEvent) => {
    e.stopPropagation();

    setFilesPreview([]);
    setSelectedFiles([]);
  };

  const isImageSelected = (url: string): boolean => {
    return filesPreview.some((file) => file.src === url);
  };

  const imageUrls = listing.imageUrls ?? [];

  return (
    <Stack gap={2}>
      <Dialog fullWidth open={open} maxWidth="md" onClose={closeModal}>
        <DialogTitle sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {translate("contactsPage.bulkMessage.uploadListingImageDialog.title")}
            </Typography>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant="body1">
              {translate("contactsPage.bulkMessage.uploadListingImageDialog.description")}
            </Typography>
            <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="center">
              {imageUrls.map((url, index) => {
                const selected = isImageSelected(url);

                return (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      width: 160,
                      height: 160,
                      borderRadius: 1
                    }}
                    onClick={handleAddImageFromListing(url)}
                  >
                    <Image
                      sx={{
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        objectFit: "cover",
                        transition: "all 200ms",
                        borderRadius: 1,
                        ...(selected && {
                          cursor: "default",
                          opacity: 0.6,
                          outlineOffset: "4px",
                          outline: (theme) => `4px solid ${theme.palette.primary.main}`
                        })
                      }}
                      alt={url}
                      src={url}
                    />
                    {selected && (
                      <IconButton
                        size="small"
                        onClick={handleRemoveImageFromListing(url)}
                        sx={{
                          m: 1,
                          top: 0,
                          right: 0,
                          color: "white",
                          border: 1,
                          position: "absolute",
                          bgcolor: "rgba(0, 0, 0, 0.4)",
                          borderColor: "divider",
                          "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.6)"
                          }
                        }}
                      >
                        <Close />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <Button color="info" variant="contained" startIcon={<PhotoLibrary />} onClick={openModal}>
        {translate("contactsPage.control.uploadImageFromListing")}
      </Button>
    </Stack>
  );
}
